import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  HsButtonModule,
  HsCardModule,
  HsIconModule,
} from '@hsetu/ngui-components';
import { Media } from '@modules/media/models/media.model';
import { ICON_DELETE, ICON_IMAGE_NEW } from '@shared/ui/icons/icon-constants';
import { HsLayerDirective } from '@hsetu/ngui-resources';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import { KfmImageType } from '@modules/media/enums/kfm-image-type.enum';
import { MediaService } from '@modules/media/services/media.service';
import { HsContact } from '@hsetu/ngui-views';
import { KfmYesNoDialogComponent } from '@shared/ui/components/features/dialogs/yes-no-dialog/kfm-yes-no-dialog.component';
import { Dialog } from '@angular/cdk/dialog';
import {
  DELETE_LOGO_LABEL,
  DELETE_LOGO_TEXT_LABEL,
} from '@modules/contact/i18n/label-text-constants';

@Component({
  selector: 'kfm-contact-logo',
  imports: [
    CommonModule,
    HsCardModule,
    HsIconModule,
    HsButtonModule,
    HsLayerDirective,
  ],
  templateUrl: './kfm-contact-logo.component.html',
})
export class KfmContactLogoComponent {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _guidGenerator = inject(GuidGeneratorService);
  private readonly _mediaService = inject(MediaService);
  private readonly _dialog = inject(Dialog);

  private readonly _deleteLogoLabel = DELETE_LOGO_LABEL;
  private readonly _deleteLogoTextLabel = DELETE_LOGO_TEXT_LABEL;

  protected readonly logo = signal<Media | null>(null);
  protected readonly logoContent = computed(() => {
    const media = this.logo();

    if (media) {
      return `data:${media.mimeType};base64,${media.content}`;
    }
  });

  protected readonly addImageIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_IMAGE_NEW);
  protected readonly deleteIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_DELETE);

  public readonly contact = input.required<HsContact>();

  constructor() {
    effect(() => {
      const contact = this.contact();

      if (!contact) {
        return;
      }

      this._mediaService.getByOwner(contact.uId).then((media) => {
        if (media && media.count > 0) {
          this.logo.set(media.data[0]);
        }
      });
    });
  }

  protected async onAddFromGalleryClicked(): Promise<void> {
    // Open gallery picker dialog
    const files = await this.openImagePicker();

    if (files && files.length > 0) {
      // Map files to Media models
      const mediaToCreate = files.map((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise<Media>((resolve) => {
          reader.onload = () => {
            const base64String = (reader.result as string).split(',')[1];
            const media: Media = {
              mimeType: file.type,
              type: KfmImageType.MandantData,
              content: base64String,
              infoDesc: file.name,
              longDesc: 'logo image to the client',
              uId: this._guidGenerator.emptyUUID,
              tenantUId: this._guidGenerator.emptyUUID,
              userUId: this._guidGenerator.emptyUUID,
              stampCreate: new Date().toISOString(),
              syncType: 0,
              concurrencyIdentificator: this._guidGenerator.emptyUUID,
              concurrencyStamp: new Date().toISOString(),
              ownerKey: this.contact().uId,
            };
            resolve(media);
          };
        });
      });

      const result = await this._mediaService.createMultiple(
        await Promise.all(mediaToCreate),
      );

      if (result) {
        this.logo.set(result[0]);
      }
    }
  }

  protected async onDeleteLogoClicked(): Promise<void> {
    const logo = this.logo();

    if (logo) {
      const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          title: this._deleteLogoLabel,
          text: this._deleteLogoTextLabel,
        },
      });

      dialogRef.closed.subscribe(async (result) => {
        if (result) {
          await this._mediaService.delete(logo.uId);
          this.logo.set(null);
        }
      });
    }
  }

  private openImagePicker(): Promise<File[]> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = false;

      input.onchange = () => {
        resolve(input.files ? Array.from(input.files) : []);
      };

      input.click();
    });
  }
}
